import { createFileRoute, useSearch, Link, useNavigate } from "@tanstack/react-router";
import { dehydrate, hydrate, HydrationBoundary, useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  organisationEmailQueryOptions,
  organisationEmailsQueryOptions,
  deleteOrganisationEmailFn,
} from "@/server/emails";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { EmailType, EmailLanguage } from "@/lib/mma/types.gen";
import { EditEmailForm } from "../../-components/EditEmailForm";
import { toast } from "sonner";
import { useState, useRef } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PlusIcon } from "lucide-react";

export const Route = createFileRoute("/_dashboard/emails/organisations/$orgId/")({
  component: OrganisationEmailIndexComponent,
  validateSearch: (search: Record<string, unknown>) => {
    const validEmailTypes: EmailType[] = [
      "registrationVerificationEmail",
      "registrationConfirmEmail",
      "registrationAdminEmail",
      "resetPasswordEmail",
    ];

    const type =
      typeof search.type === "string" && validEmailTypes.includes(search.type as EmailType)
        ? (search.type as EmailType)
        : undefined;

    const language =
      typeof search.language === "string" && search.language.length > 0
        ? (search.language as EmailLanguage)
        : undefined;

    return { type, language };
  },
  loaderDeps: ({ search }) => ({ type: search.type, language: search.language }),
  loader: async ({ params, context, deps }) => {
    const { type, language } = deps;

    if (type && language) {
      try {
        const emailQueryOptions = organisationEmailQueryOptions(Number(params.orgId), type, language);
        await context.queryClient.prefetchQuery(emailQueryOptions);
        const emailData = context.queryClient.getQueryData(emailQueryOptions.queryKey) as
          | { subject?: string }
          | undefined;
        return {
          crumb: emailData?.subject || `${type} (${language})`,
          dehydratedState: dehydrate(context.queryClient),
        };
      } catch (error) {
        return {
          crumb: `${type} (${language})`,
          dehydratedState: dehydrate(context.queryClient),
        };
      }
    }
    return {
      dehydratedState: dehydrate(context.queryClient),
    };
  },
});

function OrganisationEmailIndexComponent() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_dashboard/emails/organisations/$orgId/" });
  const { dehydratedState } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState<{ type: EmailType; language: EmailLanguage } | null>(null);
  const toastIdRef = useRef<string | number | null>(null);
  hydrate(queryClient, dehydratedState);

  const deleteEmailMutation = useMutation({
    mutationFn: (data: { orgId: number; emailType: EmailType; language: EmailLanguage }) =>
      deleteOrganisationEmailFn({ data }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organisationEmailsQueryOptions(Number(orgId)).queryKey });
      queryClient.removeQueries({ queryKey: ["email", "organisation"] });
      setShowDeleteDialog(null);
      if (toastIdRef.current !== null) {
        toast.success("Email deleted successfully", { id: toastIdRef.current });
      } else {
        toast.success("Email deleted successfully");
      }
    },
    onError: (error: any) => {
      if (toastIdRef.current !== null) {
        toast.error(error?.message || "Failed to delete email", { id: toastIdRef.current });
      } else {
        toast.error(error?.message || "Failed to delete email");
      }
    },
  });

  const emailsQuery = useQuery(organisationEmailsQueryOptions(Number(orgId)));
  const emails = emailsQuery.data || [];

  const emailQuery = useQuery({
    ...organisationEmailQueryOptions(Number(orgId), search.type as EmailType, search.language as EmailLanguage),
    enabled: !!(search.type && search.language),
  });

  const handleDeleteEmail = () => {
    if (!showDeleteDialog) return;

    toastIdRef.current = toast.loading("Deleting email...");
    deleteEmailMutation.mutate({
      orgId: Number(orgId),
      emailType: showDeleteDialog.type,
      language: showDeleteDialog.language,
    });
  };

  const hasSearchParams = !!(search.type && search.language);

  return (
    <HydrationBoundary state={dehydratedState}>
      {!hasSearchParams && (
        <>
          {emailsQuery.isLoading ? (
            <Card>
              <CardContent>
                <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Loading...</p>
              </CardContent>
            </Card>
          ) : emails.length === 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Organisation Emails</h2>
                <Button
                  onClick={() =>
                    navigate({
                      to: "/emails/organisations/$orgId/new-email",
                      params: { orgId },
                      search: { type: undefined, language: undefined },
                    })
                  }
                  variant={"outline"}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add new email
                </Button>
              </div>
              <Card>
                <CardContent className="pt-8 pb-8">
                  <p className="text-md pb-20 pt-15 text-center text-muted-foreground">
                    No organisation email templates configured. Create one to get started.
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Organisation Emails</h2>
                <Button
                  onClick={() =>
                    navigate({
                      to: "/emails/organisations/$orgId/new-email",
                      params: { orgId },
                      search: { type: undefined, language: undefined },
                    })
                  }
                  variant={"outline"}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add new email
                </Button>
              </div>
              <div className="grid gap-3">
                {emails.map((email: any) => (
                  <Card key={email.id} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">{email.name}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {email.emailType} · {email.language}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <Link
                              to="/emails/organisations/$orgId"
                              params={{ orgId }}
                              search={{ type: email.type, language: email.language }}
                            >
                              Edit
                            </Link>
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setShowDeleteDialog({ type: email.type, language: email.language })}
                            disabled={deleteEmailMutation.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {hasSearchParams && emailQuery.isLoading && (
        <Card>
          <CardContent>
            <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Loading email...</p>
          </CardContent>
        </Card>
      )}

      {hasSearchParams && !emailQuery.isLoading && emailQuery.data && (
        <EditEmailForm
          scope="organisation"
          orgId={orgId}
          email={emailQuery.data}
          emailType={search.type}
          emailLanguage={search.language}
        />
      )}

      {hasSearchParams && !emailQuery.isLoading && !emailQuery.data && (
        <Card>
          <CardContent>
            <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Email template not found.</p>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={!!showDeleteDialog}
        title="Delete Email"
        description={`Are you sure you want to delete this email? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteEmail}
        onCancel={() => setShowDeleteDialog(null)}
        loading={deleteEmailMutation.isPending}
      />
    </HydrationBoundary>
  );
}
